import type { User } from '@/types/api'

const STORAGE_KEY_PREFIX = 'soulax_last_chat_'
const LAST_ACTIVE_USER_KEY = 'soulax_last_active_user'
const LOGIN_OPENS_HOME_KEY = 'soulax_login_opens_home'

/**
 * Derives a unique key for per-user isolation based on login_id, personal_id, id, or email.
 */
export function getUserIsolationKey(
  user: User | null | undefined,
): string | null {
  if (!user) return null
  if (user.login_id && user.login_id.trim()) return user.login_id.trim()
  if (user.personal_id != null && String(user.personal_id).trim())
    return String(user.personal_id).trim()
  if (user.id && user.id.trim()) return user.id.trim()
  if (user.email && user.email.trim()) return user.email.trim()
  return null
}

/**
 * Checks if a different user has logged in since the last browser session.
 */
export function checkAndSyncActiveUser(user: User | null | undefined): {
  isDifferentUser: boolean
} {
  const currentKey = getUserIsolationKey(user)
  if (!currentKey) return { isDifferentUser: false }

  try {
    const previousKey = localStorage.getItem(LAST_ACTIVE_USER_KEY)
    const isDifferentUser = Boolean(previousKey && previousKey !== currentKey)
    localStorage.setItem(LAST_ACTIVE_USER_KEY, currentKey)
    return { isDifferentUser }
  } catch {
    return { isDifferentUser: false }
  }
}

/** Wipe leftover last-visited chat ids. Login always lands on home. */
export function clearStoredLastVisited() {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) keys.push(key)
    }
    for (const key of keys) localStorage.removeItem(key)
  } catch {
    // ignore quota / private mode
  }
}

/** Next `_app` load after Sign in must open `/`, not a leftover `/chat/$id`. */
export function markLoginOpensHome() {
  try {
    sessionStorage.setItem(LOGIN_OPENS_HOME_KEY, '1')
  } catch {
    // ignore quota / private mode
  }
}

export function consumeLoginOpensHome(): boolean {
  try {
    const marked = sessionStorage.getItem(LOGIN_OPENS_HOME_KEY) === '1'
    if (marked) sessionStorage.removeItem(LOGIN_OPENS_HOME_KEY)
    return marked
  } catch {
    return false
  }
}
